import type {
  StoreWebsiteEditorStateV3,
  StoreWebsiteEditorTemplate,
  WebsiteEditRegion,
  WebsitePageKind,
  WebsiteTemplateManifest,
} from '@dukanti/shared';

/**
 * Epic #228 P3: the editor state of one template that the active storefront
 * can edit: the template has a saved draft (its head), the draft's manifest
 * and the template itself.
 */
export interface ActiveEditorState extends StoreWebsiteEditorStateV3 {
  readonly templateId: string;
  readonly head: NonNullable<StoreWebsiteEditorStateV3['head']>;
  readonly draft: NonNullable<StoreWebsiteEditorStateV3['draft']>;
  readonly manifest: WebsiteTemplateManifest;
  readonly template: StoreWebsiteEditorTemplate;
}

export function toActiveEditorState(state: StoreWebsiteEditorStateV3): ActiveEditorState | null {
  const { templateId, head, draft, manifest, template } = state;
  if (templateId === null || head === null || draft === null) return null;
  if (manifest === null || template === null) return null;
  return { ...state, templateId, head, draft, manifest, template };
}

/** The page a region belongs to, or null for chrome (it is on every page). */
export function pageKindOfRegion(region: WebsiteEditRegion): WebsitePageKind | null {
  return region.scope === 'chrome' ? null : region.scope;
}

/**
 * True once the store shows customers something: a published template (any
 * template, not only this one), or the frozen legacy page of a converted store.
 */
export function isLive(state: StoreWebsiteEditorStateV3): boolean {
  return state.live !== null || state.mode === 'legacy';
}

/**
 * Shoppers see the store's original builder design: the store is still in
 * legacy mode AND went through the conversion (`converted`, or `unmappable`
 * when its design could not be converted). A brand-new store is also in
 * legacy mode, but with `conversion: 'none'` it has no original design live;
 * it must not be treated as converted.
 */
export function isLegacyDesignLive(
  state: Pick<StoreWebsiteEditorStateV3, 'mode' | 'conversion'>,
): boolean {
  return state.mode === 'legacy' && state.conversion !== 'none';
}
